import React, {useContext, useEffect, useMemo, useState} from "react";
import { Container, Row, Col, Button, Alert, Form, ProgressBar } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import SEO from "../../components/seo/SEO";

import { verifyEmail, resendVerification } from "../../http/userAPI";
import {USER_DASHBOARD_ROUTE, VERIFY_EMAIL_ROUTE} from "../../utils/consts";
import {Context} from "../../index";
import { toast } from "react-toastify";

const VerifyEmail = () => {
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const token = sp.get("token");
    const { user } = useContext(Context);

    const [status, setStatus] = useState(token ? "verifying" : "waiting");
    // waiting | verifying | success | error
    const [errorMsg, setErrorMsg] = useState("");
    const [email, setEmail] = useState(
        (localStorage.getItem("pendingVerifyEmail") || "").toLowerCase()
    );
    const [resendCooldown, setResendCooldown] = useState(59);

    // наверху компонента:
    const [progress, setProgress] = useState(0);
    const stepsTexts = [
        "Setting up the environment…",
        "Allocating space for your books…",
        "Preparing your library…",
        "Loading polls…",
        "Finalizing settings…",
        "Done! Welcome aboard."
    ];
    const [stepIdx, setStepIdx] = useState(0);


    useEffect(() => {
        if (resendCooldown <= 0) return;
        const id = setInterval(() => {
            setResendCooldown((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => clearInterval(id);
    }, [resendCooldown]);

    useEffect(() => {
        if (!token) return;

        let rafId = null;
        const DURATION = 12000;                 // 3 секунды
        const STEP_COUNT = stepsTexts.length;  // 6 шагов
        const STEP_MS = DURATION / STEP_COUNT; // ~500 мс на шаг

        (async () => {
            setStatus("verifying");
            try {
                const data = await verifyEmail(token);
                setStatus("success");
                setProgress(0);
                setStepIdx(0);

                const start = performance.now();

                const tick = (t) => {
                    const elapsed = t - start;
                    const pct = Math.min(100, Math.round((elapsed / DURATION) * 100));
                    setProgress(pct);

                    // смена текста раз в ~500 мс
                    const idx = Math.min(
                        STEP_COUNT - 1,
                        Math.floor(elapsed / STEP_MS)
                    );
                    setStepIdx(idx);

                    if (elapsed < DURATION) {
                        rafId = requestAnimationFrame(tick);
                    } else {
                        // финальный рывок — и уходим в дашборд
                        user.setUser(data);
                        user.setIsAuth(true);
                        setProgress(100);
                        setStepIdx(STEP_COUNT - 1);
                        navigate(USER_DASHBOARD_ROUTE);
                        user?.setActiveComponent('MainPageDashboard');
                    }
                };

                rafId = requestAnimationFrame(tick);
            } catch (e) {
                setErrorMsg(e?.response?.data?.message || "Verification link is invalid or expired.");
                setStatus("error");
            }
        })();

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [token]);

    useEffect(() => {
        const COOLDOWN_SECONDS = 5;
        const untilStr = localStorage.getItem('verifyCooldownUntil');
        const until = untilStr ? parseInt(untilStr, 10) : 0;
        const now = Date.now();

        // если в хранилище есть «когда можно», считаем остаток; иначе запускаем 59
        const remaining = until > now
            ? Math.ceil((until - now) / 1000)
            : COOLDOWN_SECONDS;

        setResendCooldown(remaining);
    }, []);

    // кнопка «отправить заново»
    const onResend = async (e) => {
        e?.preventDefault?.();
        if (resendCooldown > 0) return;

        if (!email) {
            setErrorMsg("Please enter your email.");
            return;
        }
        setErrorMsg("");

        try {
            const resp = await resendVerification(email);
            const COOLDOWN_SECONDS = 59;
            const until = Date.now() + COOLDOWN_SECONDS * 1000;
            localStorage.setItem('verifyCooldownUntil', String(until));
            setResendCooldown(COOLDOWN_SECONDS);
            const message =
                (typeof resp === "string" ? resp : resp?.message) ||
                `Verification email re-sent to ${email}. Check inbox & spam.`;
            toast.success(message, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || "Cannot resend now. Try later.");
        }
    };

    const seo = useMemo(
        () => ({
            title: "Verify your email – Virtalibry",
            description:
                "Confirm your email to activate your Virtalibry account. If you didn’t receive the email, check spam or resend the verification link.",
            keywords:
                "virtalibry verify email, account activation, confirmation link, resend verification",
            url: "https://virtalibry.com/verify-email",
            canonical: "https://virtalibry.com/verify-email",
            image: "https://virtalibry.com/face/why_love_is_gold_new.png",
            type: "website",
            webPage: {
                name: "Verify Email",
                url: "https://virtalibry.com/verify-email",
                description:
                    "Finish registration by confirming your email address to activate your Virtalibry account.",
            },
            webApp: {
                type: "WebApplication",
                name: "Virtalibry Author Tools",
                url: "https://virtalibry.com/verify-email",
                applicationCategory: "ProductivityApplication",
                operatingSystem: "All",
                offers: { price: "0", priceCurrency: "USD", url: "https://virtalibry.com/verify-email" },
                publisher: "Virtalibry",
            },
            breadcrumb: [
                { name: "Virtalibry Home", item: "https://virtalibry.com" },
                { name: "Verify Email", item: "https://virtalibry.com/verify-email" },
            ],
            faq: [
                {
                    q: "I didn’t receive the verification email. What should I do?",
                    a: "Check your spam folder and ensure you entered the correct address. You can also resend the verification email from this page.",
                },
                {
                    q: "How long is the verification link valid?",
                    a: "The verification link is typically valid for 24 hours. If it expires, you can request a new one.",
                },
            ],
        }),
        []
    );

    return (
        <>
            <SEO
                title={seo.title}
                description={seo.description}
                keywords={seo.keywords}
                url={seo.url}
                canonical={seo.canonical}
                image={seo.image}
                type={seo.type}
                webPage={seo.webPage}
                webApp={seo.webApp}
                breadcrumb={seo.breadcrumb}
                faq={seo.faq}
            />

            <Container fluid className="p-0">
                <div
                    className="d-flex flex-column justify-content-start align-items-center pt-5 pb-5"
                    style={{
                        backgroundImage: `linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)), url("/face/face_img_23.jpg")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        marginTop: -30,
                    }}
                >
                    {/* Заголовок */}
                    <Row className="text-center mb-3 mt-2 w-100" style={{ maxWidth: 960 }}>
                        <Col>
                            <h3 className={`mb-2 ${status === 'success' ? 'text-success' : 'text-dark'}`}>
                                {status === 'success' ? 'Setting up your account…' : 'Verify Your E-Mail'}
                            </h3>

                            <h6 className="text-muted mb-0">
                                {status === 'success' ? stepsTexts[stepIdx] : (
                                    <>
                                        Confirm your E-Mail address to Activate <br />
                                        your Account and get Welcome Bonus.
                                    </>
                                )}
                            </h6>
                        </Col>
                    </Row>


                    {/* Контент */}
                    <Row className="justify-content-center w-100" style={{ maxWidth: 960 }}>
                        <Col xs={12} md={8} lg={7} xl={6}>
                            {/* waiting */}
                            {status === "waiting" && (
                                <div className="card shadow-sm border border-secondary" style={{ background: 'rgba(255,255,255,0.98)' }}>
                                    <div className="card-body">
                                        <p className="mb-2">
                                            Great — your account is almost ready! We’ve just sent an activation link to
                                            <strong> {email} </strong>. Please open your inbox and find our message
                                            (and check the <em>Spam</em> or <em>Promotions</em> folder just in case).
                                            Click <em>Verify Email</em> in that message to finish creating your Virtalibry account.
                                        </p>
                                        <p className="mb-3">
                                            Didn’t get it? Wait a minute, then press <strong>Send Again</strong> below, or make sure the address is correct.
                                        </p>

                                        {errorMsg && (
                                            <Alert variant="danger" className="py-2">
                                                {errorMsg}
                                            </Alert>
                                        )}

                                        <Form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                onResend();
                                            }}
                                        >
                                            {/* моб: столбик; десктоп: в одну линию */}
                                            <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center">
                                                <Form.Control
                                                    type="email"
                                                    placeholder="your@email.com"
                                                    value={email}
                                                    disabled={true}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    aria-label="Your email"
                                                    style={{ minWidth: 0 }}  // чтобы инпут не выдавливал кнопку
                                                    className="flex-grow-1 border border-secondary"
                                                />

                                                {/* кнопка 100% ширины на мобилке; на md+ — авто; и не сжимается */}
                                                <div className="d-grid d-md-block flex-shrink-0">
                                                    <Button
                                                        type="submit"
                                                        variant="success"
                                                        className="text-nowrap px-3"
                                                        onClick={onResend}
                                                        disabled={resendCooldown > 0}
                                                    >
                                                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Send Again"}
                                                    </Button>
                                                </div>
                                            </div>
                                        </Form>
                                    </div>
                                </div>

                            )}

                            {/* verifying */}
                            {status === "verifying" && (
                                <div className="card shadow-sm border-0" style={{ background: 'rgba(255,255,255,0.98)' }}>
                                    <div className="card-body">
                                        <p className="mb-2">Verifying your email…</p>
                                        <ProgressBar
                                            now={progress}
                                            striped
                                            animated
                                            variant="success"
                                            className="mb-1"
                                            style={{ height: 10 }}
                                        />
                                    </div>
                                </div>
                            )}

                            {status === "success" && (
                                <div className="card shadow-sm border border-secondary" style={{ background: 'rgba(255,255,255,0.98)' }}>
                                    <div className="card-body">
                                        <p className="mb-2 fw-semibold">{stepsTexts[stepIdx]}</p>
                                        <ProgressBar
                                            now={progress}
                                            striped
                                            animated
                                            variant="success"
                                            className="mb-1"
                                            style={{ height: 10 }}
                                        />
                                        <small className="text-muted">
                                            {progress < 100
                                                ? "Please don’t close the page…"
                                                : "All set! Redirecting to your dashboard."
                                            }
                                        </small>
                                    </div>
                                </div>
                            )}

                            {/* error */}
                            {status === "error" && (
                                <div className="card shadow-sm border-0" style={{ background: 'rgba(255,255,255,0.98)' }}>
                                    <div className="card-body">
                                        <Alert variant="danger" className="py-2">
                                            {errorMsg || "Verification link is invalid or expired."}
                                        </Alert>
                                        <p className="mb-2">
                                            You can request a new verification email. Make sure you enter the correct address and check your spam folder.
                                        </p>


                                        <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center">
                                            <Form.Control
                                                type="email"
                                                placeholder="your@email.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                aria-label="Your email"
                                                style={{ minWidth: 0 }}  // чтобы инпут не выдавливал кнопку
                                                className="flex-grow-1 border border-secondary"
                                            />

                                            {/* кнопка 100% ширины на мобилке; на md+ — авто; и не сжимается */}
                                            <div className="d-grid d-md-block flex-shrink-0">
                                                <Button
                                                    type="submit"
                                                    variant="success"
                                                    className="text-nowrap px-3"
                                                    onClick={onResend}
                                                    disabled={resendCooldown > 0}
                                                >
                                                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Send Again"}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            </Container>

        </>
    );
};

export default VerifyEmail;
